import React, { useCallback, useMemo, useState } from 'react';

import {
	ActionSheet,
	ActionSheetAction,
} from '@/components/shared/ActionSheet';
import { Loader } from '@/components/shared/Loader';
import {
	SettingCard,
	SettingsList,
	SettingsListItems,
} from '@/components/shared/SettingCard';
import { useDeleteTeamSongMutation } from '@/graphql';
import { useAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { SongData } from '@/providers/AnalyticsProvider/events';
import { useTeamSongs } from '@/providers/TeamSongsProvider';
import { Divider, List } from '@material-ui/core';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import {
	ESongModalDisplayState,
	IPropsSongModalDisplay,
} from '../SongModalPanel';
import styles from './SongSettings.module.scss';


export const SongSettings: React.FC<IPropsSongModalDisplay> = ({
	pushDisplayState,
	openedSong,
	popDisplayState,
	setOpen,
}) => {
	const { mutateAsync: deleteTeamSong, isLoading } =
		useDeleteTeamSongMutation();
	const { enqueueSnackbar } = useSnackbar();
	const [actionOpen, setActionOpen] = useState(false);
	const { refetch } = useTeamSongs();

	const isSynced = !!openedSong.originSongId;

	const fireRemoveSongEvent = useAnalyticsEvent('View_Song_Remove_Song');
	const fireViewSongInformationEvent = useAnalyticsEvent(
		'View_Song_View_Song_Information',
	);

	const handleDialogClose = () => setActionOpen(false);

	const eventData = useMemo<SongData>(
		() => ({
			id: openedSong?.id || '',
			artist: openedSong?.artist || '',
			title: openedSong?.name || '',
		}),
		[openedSong],
	);

	const handleRemove = useCallback(async () => {
		try {
			if (openedSong?.id) {
				await deleteTeamSong({
					teamSongId: openedSong.id,
				});
				setOpen(false);
				enqueueSnackbar('Song removed from the songlist', {
					variant: 'success',
				});
				fireRemoveSongEvent(eventData);
				refetch();
				popDisplayState();
			}
		} catch (e) {
			enqueueSnackbar(
				'Something went wrong while updating the songlist',
				{
					variant: 'error',
				},
			);
			return;
		}
	}, [
		enqueueSnackbar,
		openedSong?.id,
		popDisplayState,
		eventData,
		fireRemoveSongEvent,
		deleteTeamSong,
		refetch,
		setOpen,
	]);

	if (!openedSong) {
		return <Loader />;
	}

	const additionalItems: SettingsListItems = [
		{
			type: 'setting',
			title: 'Edit song',
			condition: !isSynced,
			onClick: () => {
				pushDisplayState(ESongModalDisplayState.SONG_INFORMATION);
				fireViewSongInformationEvent();
			},
		},
	];

	return (
		<div className={styles['song-settings']}>
			{!isSynced && (
				<>
					<SettingsList
						className={styles['song-settings__upper-list']}
						items={additionalItems}
						noChevron
					/>
					<Divider />
				</>
			)}
			<List className={styles['song-settings__lower-list']}>
				<SettingCard
					type="setting"
					title="Remove from your songs"
					onClick={() => setActionOpen(true)}
					red
					noChevron
					loading={isLoading}
				/>
			</List>
			<Divider />

			<ActionSheet
				open={actionOpen}
				title={
					<span>
						Are you sure you want to remove{' '}
						<strong>{openedSong.name}</strong> by{' '}
						<strong>{openedSong.artist}</strong> from your songlist?
					</span>
				}
				onClose={handleDialogClose}
			>
				<ActionSheetAction
					label={'Remove for everyone'}
					onActionClick={() => {
						handleRemove();
					}}
					type={'danger'}
				/>
			</ActionSheet>
		</div>
	);
};
